import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    HostBinding,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    TemplateRef,
    TrackByFunction,
    inject,
} from '@angular/core';

import { EventOptionGroup, VerticalDesignOptions } from '@frontend/sports/betting-offer/feature/model';
import { EventDetailsConfig, GridConfig as MsGrid, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { hasTarget } from '@frontend/sports/common/core/utils/dom';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { GRID_CONFIG } from '@frontend/sports/grid/core/feature/config';
import { GridEvent, GridMedia, Group } from '@frontend/sports/grid/core/feature/model';
import { TrackingSource } from '@frontend/sports/tracking/feature';
import { DeviceService, NativeAppService } from '@frontend/vanilla/core';
import { keys } from 'lodash-es';

import { EpcotConfigService, EpcotModule } from '../../common/epcot-config.service';
import { OptionGroupService } from '../../event-model/helpers/option-group.service';
import { RedirectHelperService } from '../../navigation-core/redirect-helper.service';
import { EventLinkConfig, UrlHelperService } from '../../navigation-core/url-helper.service';
import { StatisticsConfigService } from '../../statistics/statistics-config.service';
import { GameNotesService } from '../game-notes.service';

@Component({
    selector: 'ms-event',
    templateUrl: 'event.component.html',
    styleUrls: ['./event.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventComponent implements OnInit, OnChanges {
    @Input() event: GridEvent;
    @Input() groups: Group[];
    @Input() tracking: TrackingSource;
    @Input() linkConfig: EventLinkConfig;
    @Input() media: GridMedia;
    @Input() favouriteToggle: boolean;
    @Input() promotedFixtureIdsMap: { [fixtureId: number]: number } = {};
    @Input() optionGroupTemplate: TemplateRef<any>;
    @Input() showParticipantImage: boolean;
    @Input() calendarInfoDisplay = false;
    @Input() showOptionname: boolean;
    @Input() showAllWagers: boolean | undefined;
    @Input() betBuilderEpcotEnabled = false;
    @Input() betDetails = '';
    @Input() gameNotes?: string;
    @Input() showLogoColor: boolean;

    @Output() moreBetsClicked = new EventEmitter<void>();
    @Output() statsClicked = new EventEmitter<void>();

    @HostBinding('class') className = 'grid-event ms-active-highlight';

    @HostBinding('class.one-groupoptions')
    get threeColumn(): boolean {
        return !this.groups || this.groups.length === 1;
    }

    @HostBinding('class.has-fallback-market') hasFallbackMarket = false;

    @HostBinding('class.all-wagers-absent')
    get eventFooterAbsentClass(): boolean {
        return !this.isAllMarketsLinkEnabled;
    }

    eventUrl: string;
    shouldRemoveHref: Nullable<boolean>;
    empty: boolean;
    emptyText: string;
    betDetailsSource = '';
    groupsMap: { [key: string]: EventOptionGroup };
    hasBetsCountButton = inject(GRID_CONFIG, { optional: true })?.hasBetsCountButton ?? false;
    showParticipantStats = false;
    trackingSource: TrackingSource;
    fallbackMarketName = '';
    showCenterOptionName = false;

    isAllMarketsLinkEnabled: boolean;
    gridEventDisplayOverride = this.msGrid.gridEventDisplayVersion === 'V2';
    iconSize = this.gridEventDisplayOverride ? '12' : '13';

    readonly isEpcotEnabled = inject(EpcotConfigService).isEnabled(EpcotModule.Grid);
    readonly isTwoThreeWayEnabled = this.isEpcotEnabled && this.msGrid.is2way3wayEnabled;
    readonly showSuspendedSelection = this.eventDetailsConfig.isSuspendedSelectionVisibilityEnabled;

    constructor(
        public sitecore: Sitecore,
        public msGrid: MsGrid,
        private urlHelper: UrlHelperService,
        private optionGroupService: OptionGroupService,
        private deviceService: DeviceService,
        private nativeAppService: NativeAppService,
        private redirectHelper: RedirectHelperService,
        private gameNotesService: GameNotesService,
        private statisticsConfigService: StatisticsConfigService,
        private eventDetailsConfig: EventDetailsConfig,
    ) {
        this.isAllMarketsLinkEnabled = this.msGrid.isAllMarketsLinkEnabled;
    }

    get betsCountMessage(): string {
        return interpolateDoubleBracesString(this.sitecore.eventGrid.BetsLinkText, { count: this.event.totalMarketsCount });
    }

    ngOnInit(): void {
        this.showParticipantStats = this.isEpcotEnabled
            ? this.statisticsConfigService.showStats(this.event.sport.id) && this.msGrid.is2way3wayEnabled
            : this.statisticsConfigService.showStats(this.event.sport.id) && this.msGrid.isAllMarketsLinkEnabled;
        this.shouldRemoveHref = this.urlHelper.shouldRemoveHref();
        if (this.isTwoThreeWayEnabled && this.event) {
            this.gameNotes = this.gameNotesService.getGameNotes(this.event.id);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.groups || changes.event) {
            this.setOptionGroup();
            this.setLink();
        }

        if (changes.linkConfig) {
            this.setLink();
        }

        this.betDetailsSource = this.hasFallbackMarket ? 'fallback market' : this.betDetails;

        if (changes.tracking) {
            this.trackingSource = this.getTracking();
        }

        if (this.showAllWagers !== undefined) {
            this.isAllMarketsLinkEnabled = this.msGrid.isAllMarketsLinkEnabled && this.showAllWagers;
        }
    }

    trackOptionGroup: TrackByFunction<EventOptionGroup> = (_, optionGroup) => optionGroup.id;

    eventClick(event: Event): void {
        if (!this.deviceService.isTouch && !this.nativeAppService.isTerminal) {
            if (hasTarget(event, HTMLAnchorElement)) {
                return;
            }

            this.redirectHelper.goToPage(this.eventUrl);
        }
    }

    eventInfoClick(): void {
        this.redirectHelper.goToPage(this.eventUrl);
    }

    private setLink(): void {
        this.eventUrl = this.urlHelper.getEventUrl(this.event, this.linkConfig.extraParams);
        this.emptyText = this.linkConfig.emptyText;
    }

    overriddenMarketHeader(group: Group): string | undefined {
        if (!group) {
            return undefined;
        }
        const verticalDesignModel = this.getVerticalDesignOptions(group);

        if (!verticalDesignModel?.isVerticalDesignEnabled) {
            return undefined;
        } else {
            return this.sitecore.eventGridMarketHeaders.messages[group.id] || ' ';
        }
    }

    getVerticalDesignOptions(group: Group): VerticalDesignOptions {
        const verticalDesignModel = {} as VerticalDesignOptions;
        const verticalGroupConfig = this.msGrid.verticalGridGroupIds[group.id];
        if (this.isTwoThreeWayEnabled && (group.options?.length === 2 || verticalGroupConfig)) {
            verticalDesignModel.isVerticalDesignEnabled = true;
            verticalDesignModel.showOptionAttributeDecorator = !!(verticalGroupConfig && group.balancedMarket);
            if (!group.balancedMarket) {
                verticalDesignModel.showOptionName = !!verticalGroupConfig?.showOptionName;
            }
        }

        return verticalDesignModel;
    }

    private setOptionGroup(): void {
        this.groupsMap = {};

        if (!this.showSuspendedSelection && !this.groups) {
            this.empty = true;
        } else {
            this.groups.forEach((group) => {
                let optionGroup = this.optionGroupService.get(this.event.optionGroups, group.id);
                let fallbackInfo;
                if (this.msGrid.fallbackMarketsEnabled && this.groups.length === 1 && this.event.live) {
                    fallbackInfo = this.optionGroupService.getFallbackOptionGroup(group, this.event, optionGroup);

                    if (fallbackInfo?.optionGroup) {
                        optionGroup = fallbackInfo.optionGroup;
                    }

                    this.hasFallbackMarket = fallbackInfo?.isFallbackMarket ?? false;
                    this.fallbackMarketName = fallbackInfo?.fallbackMarketName ?? '';
                    this.showCenterOptionName = fallbackInfo?.isNextGoalMarket ?? false;
                }
                if (optionGroup) {
                    this.groupsMap[group.id] = optionGroup;
                }
            });

            const optionGroups = keys(this.groupsMap || {});
            this.empty =
                !this.showSuspendedSelection &&
                (optionGroups.length === 0 || optionGroups.every((x) => !this.groupsMap[x] || !this.groupsMap[x].online));
        }
    }

    private getTracking(): TrackingSource {
        if (this.hasFallbackMarket) {
            const lastIndex = this.tracking.source?.lastIndexOf('/');
            const source = this.tracking.source?.substring(0, lastIndex);

            return {
                source: `${source}/Gridable/${this.betDetailsSource}`,
            };
        }

        return this.tracking;
    }

    trackMoreBets(event: Event): void {
        this.moreBetsClicked.emit();
        event.stopPropagation();
    }
}
